'use client';

import { useEffect, useMemo, useState } from 'react';

import type { MetricId, PizzaModelId, SeriesResponse } from '@/lib/types';

type UsePizzaSeriesResult = {
  data: SeriesResponse | null;
  isLoading: boolean;
  error: string | null;
};

export function usePizzaSeries(metric: MetricId, pizzaModel: PizzaModelId): UsePizzaSeriesResult {
  const [data, setData] = useState<SeriesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const endpoint = useMemo(() => {
    const base = process.env.NEXT_PUBLIC_API_BASE || '/api';
    const url = new URL(`${base.replace(/\/$/, '')}/series`, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    url.searchParams.set('metric', metric);
    url.searchParams.set('pizzaModel', pizzaModel);
    return url.pathname + url.search;
  }, [metric, pizzaModel]);

  useEffect(() => {
    const ctrl = new AbortController();

    async function run() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(endpoint, { signal: ctrl.signal });
        if (!response.ok) {
          throw new Error(`API error (${response.status})`);
        }
        const payload = (await response.json()) as SeriesResponse;
        setData(payload);
      } catch (err) {
        if (!ctrl.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Unexpected error');
        }
      } finally {
        if (!ctrl.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    run();
    return () => ctrl.abort();
  }, [endpoint]);

  return { data, isLoading, error };
}
